import { useState, useEffect } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import validator from 'validator';
import { isEmpty } from 'lodash';
import type { VenueUpgradeDetailsQuery } from '@pv/common/graphql/operations';
import { OrganizationUpgradeDetailsQuery } from '@pv/common/graphql/operations';
type Venue = VenueUpgradeDetailsQuery['venue'];
type PaymentMethod =
  OrganizationUpgradeDetailsQuery['organization']['paymentMethods'][number];
interface UsePaymentProcessingParams {
  venue: Venue | null;
  paymentMethods: PaymentMethod[];
  organizationBillingEmail: string | undefined;
  open: boolean;
  user?: { fullName?: string } | null;
}

interface ValidationResult {
  valid: boolean;
  error?: string;
}

interface UsePaymentProcessingReturn {
  billingEmail: string | undefined;
  setBillingEmail: (email: string | undefined) => void;
  stripeLoading: boolean;
  setStripeLoading: (loading: boolean) => void;
  selectedPaymentMethodId: string | undefined;
  setSelectedPaymentMethodId: (id: string | undefined) => void;
  cardholderName: string;
  setCardholderName: (name: string) => void;
  getStripePaymentMethodToken: () => Promise<string>;
  validateBillingInfo: () => ValidationResult;
}

/**
 * Hook for managing payment processing (Stripe tokens and payment methods)
 */
export const usePaymentProcessing = ({
  venue,
  paymentMethods,
  organizationBillingEmail,
  open,
  user,
}: UsePaymentProcessingParams): UsePaymentProcessingReturn => {
  const stripe = useStripe();
  const elements = useElements();
  const [billingEmail, setBillingEmail] = useState<string | undefined>();
  const [stripeLoading, setStripeLoading] = useState<boolean>(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | undefined
  >(venue?.subscription?.stripeSourceId || undefined);
  const [cardholderName, setCardholderName] = useState<string>(
    user?.fullName || ''
  );

  // Initialize billing email from organization
  useEffect(() => {
    setBillingEmail(organizationBillingEmail || undefined);
  }, [open, organizationBillingEmail]);

  // Initialize payment method selection
  useEffect(() => {
    if (venue?.subscription?.stripeSourceId) {
      setSelectedPaymentMethodId(
        venue.subscription.stripeSourceId || undefined
      );
    } else if (!isEmpty(paymentMethods)) {
      const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault);
      if (defaultPaymentMethod) {
        setSelectedPaymentMethodId(defaultPaymentMethod.id);
      } else {
        setSelectedPaymentMethodId(paymentMethods[0]?.id);
      }
    }
  }, [open, venue?.subscription?.stripeSourceId, paymentMethods]);

  /**
   * Get Stripe payment method token from card element
   */
  const getStripePaymentMethodToken = async (): Promise<string> => {
    if (!stripe || !elements) {
      throw new Error('Stripe is not initialized');
    }
    const cardElement = elements.getElement('card');
    if (!cardElement) {
      throw new Error('Card element not found');
    }
    const { token, error } = await stripe.createToken(cardElement, {
      name: cardholderName || billingEmail || 'none',
    });
    if (error) {
      throw new Error(error.message);
    }
    if (!token) {
      throw new Error('Failed to create payment token');
    }
    return token.id;
  };

  /**
   * Validate billing information
   */
  const validateBillingInfo = (): ValidationResult => {
    if (isEmpty(paymentMethods)) {
      if (!billingEmail || !validator.isEmail(billingEmail)) {
        return { valid: false, error: 'Please enter a valid billing email' };
      }
    }
    return { valid: true };
  };

  return {
    billingEmail,
    setBillingEmail,
    stripeLoading,
    setStripeLoading,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    cardholderName,
    setCardholderName,
    getStripePaymentMethodToken,
    validateBillingInfo,
  };
};
